import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AdminNav from '../../components/admin/AdminNav';
import { toast } from '../../utils/toast';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../utils/api';
import { getCurrentUser } from '../../utils/auth';
import { FaSearch, FaEdit, FaTrash, FaInfoCircle } from 'react-icons/fa';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import UserInfoModal from '../../components/common/UserInfoModal';
import SupplierInfoModal from '../../components/common/SupplierInfoModal';
import TruncatedText from '../../components/common/TruncatedText';
import InfoModal from '../../components/common/InfoModal';
import Pagination from '../../components/common/Pagination';
import { useIsMobile } from '../../utils/useIsMobile';
import ResultCount from '../../components/common/ResultCount';
import { NAME_LIMITS } from '../../utils/nameLimits';

const USERS_PER_PAGE = 5;

// A supplier may arrive as an ID or as a populated object
const idOf = (supplier) => (supplier && typeof supplier === 'object' ? supplier._id : supplier);

// All supplier IDs a user is linked to, primary first, without duplicates
const linkedIdsOf = (user) => [
  ...new Set([user?.supplierId, ...(user?.supplierIds || [])].filter(Boolean).map(idOf)),
];

export default function AdminUsersPage() {
  const { t } = useTranslation();
  const currentUser = getCurrentUser();
  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isSupplierAdmin = currentUser?.role === 'supplieradmin';
  const currentUserId = currentUser?._id || currentUser?.userId;

  // State
  const [users, setUsers] = useState([]);
  const [delegationUser, setDelegationUser] = useState(null); // user whose delegated groups are listed
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  // Supplier IDs for the form; the first one is the user's primary supplier
  const [selectedSupplierIds, setSelectedSupplierIds] = useState(
    isSupplierAdmin && currentUser?.supplierId ? [currentUser.supplierId] : []
  );
  const [editingUser, setEditingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();
  const [expandedCardId, setExpandedCardId] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  // Supplier the admin tried to uncheck on an existing user; removed only after confirming
  const [pendingSupplierRemoval, setPendingSupplierRemoval] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Suppliers the signed-in supplier admin is linked to (all of them, not just the primary)
  const [mySuppliers, setMySuppliers] = useState([]);
  // Superadmins pick from all suppliers; supplier admins only from their own
  const supplierOptions = isSuperAdmin ? suppliers : mySuppliers;

  const indexOfLastUser = currentPage * USERS_PER_PAGE;
  const indexOfFirstUser = indexOfLastUser - USERS_PER_PAGE;
  // Underlying value per sortable column, so sorting ignores formatted display text
  const SORT_VALUES = {
    username: (u) => (u.username || '').toLowerCase(),
    email: (u) => (u.email || '').toLowerCase(),
    role: (u) => u.role || '',
    supplier: (u) => linkedIdsOf(u)
      .map((id) => supplierOptions.find((s) => s._id === id)?.name || '')
      .filter(Boolean)
      .sort()
      .join(', ')
      .toLowerCase(),
  };
  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return filteredUsers;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const valueOf = SORT_VALUES[sortConfig.key];
    return [...filteredUsers].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
    // SORT_VALUES is rebuilt each render; the sort only depends on the inputs below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredUsers, sortConfig, supplierOptions]);
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(sortedUsers.length / USERS_PER_PAGE);

  const sortByKey = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };
  const sortArrow = (key) => <span className="inline-block w-3 text-center">{sortConfig.key !== key ? '' : sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/users');
      const list = Array.isArray(res.data) ? res.data : [];
      setUsers(list);
      setFilteredUsers(list);
      setCurrentPage(1);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      toast.error(err.response?.data?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch suppliers
  const fetchSuppliers = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await api.get('/api/suppliers');
      const list = Array.isArray(res.data) ? res.data : [];
      setSuppliers(list);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
      toast.error(err.response?.data?.message || 'Failed to load suppliers.');
    }
  }, [isSuperAdmin]);

  // Fetch the supplier admin's linked suppliers
  useEffect(() => {
    if (!isSuperAdmin) {
      api.get('/api/suppliers/my-suppliers')
        .then(res => setMySuppliers(Array.isArray(res.data) ? res.data : []))
        .catch(err => console.error('Failed to fetch my suppliers', err));
    }
  }, [isSuperAdmin]);

  // Initial fetch
  useEffect(() => {
    fetchUsers();
    fetchSuppliers();
  }, [fetchUsers, fetchSuppliers]);

  // Search
  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      const lower = query.toLowerCase();
      setFilteredUsers(users.filter((u) => u.username?.toLowerCase().includes(lower)));
      setCurrentPage(1);
    },
    [users]
  );

  useEffect(() => {
    if (searchQuery === '') setFilteredUsers(users);
  }, [users, searchQuery]);

  // Reset form
  const resetForm = () => {
    setName('');
    setEmail('');
    setRole('member');
    setSelectedSupplierIds(isSupplierAdmin && currentUser?.supplierId ? [currentUser.supplierId] : []);
    setEditingUser(null);
  };

  // Submit user form
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email) {
      toast.error(t('adminUsers.usernameEmailRequired'));
      return;
    }
  // Suppliers this user will be linked to. Supplier admins keep any existing links to suppliers
  // outside their own list, since they can only change links to their own suppliers.
  const mySupplierIds = mySuppliers.map((s) => s._id);
  const keptOutsideIds =
    !isSuperAdmin && editingUser
      ? linkedIdsOf(editingUser).filter((id) => !mySupplierIds.includes(id))
      : [];
  const chosenSupplierIds = [...new Set([...selectedSupplierIds, ...keptOutsideIds])];

  // ⚠️ Validation before sending to backend
  if ((role === 'supplieradmin' || role === 'member') && chosenSupplierIds.length === 0) {
    toast.error(t('adminUsers.supplierRequired'));
    return;
  }

    const payload = {
      username: name.trim(),
      email: email.trim(),
      role,
      password: '', // backend generates/reset password
      autoApprove: role !== 'member',
    };
   // Only set suppliers if the role is not superadmin
   if (role !== 'superadmin') {
      payload.supplierId = chosenSupplierIds[0];
      payload.supplierIds = chosenSupplierIds.slice(1);
    }
console.error('Submitting payload:', payload);

    try {
      let res;
      if (editingUser) {
        res = await api.patch(`/api/users/${editingUser._id}`, payload);
        const updated = res.data;
        toast.success(t('adminUsers.updated', { name: updated.user.username }));
        setUsers(prev => prev.map(u => (u._id === updated._id ? updated : u)));
        setFilteredUsers(prev => prev.map(u => (u._id === updated._id ? updated : u)));
      } else {
        res = await api.post('/api/users/admin-create', payload);
        const created = res.data;
        toast.success(t('adminUsers.added', { name: created.user.username, email: created.user.email }));
        setUsers(prev => [created, ...prev]);
        setFilteredUsers(prev => [created, ...prev]);
      }
      resetForm();
      fetchUsers();
    } catch (err) {
      console.error('Save user failed:', err);
      toast.error(err.response?.data?.message || 'Failed to save user.');
    }
  };

  // Edit user
  const handleEdit = (user) => {
    if (user._id === currentUserId) return;
    setEditingUser(user);
    setName(user.username);
    setEmail(user.email || '');
    setRole(user.role || 'member');
    // Supplier admins only see/change links to their own suppliers
    setSelectedSupplierIds(
      isSuperAdmin
        ? linkedIdsOf(user)
        : linkedIdsOf(user).filter((id) => mySuppliers.some((s) => s._id === id))
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Delete user
  const handleDeleteClick = (user) => {
    if (user._id === currentUserId) return;
    setUserToDelete(user);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await api.delete(`/api/users/${userToDelete._id}`);
      setUsers(prev => prev.filter(u => u._id !== userToDelete._id));
      setFilteredUsers(prev => prev.filter(u => u._id !== userToDelete._id));
      toast.success(t('adminUsers.deleted', { name: userToDelete.username }));
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(err.response?.data?.message || 'Failed to delete user.');
    } finally {
      setShowConfirm(false);
      setUserToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setUserToDelete(null);
  };

  // Supplier removal (edit form): the link is only dropped from the form here; saving applies it
  const confirmSupplierRemoval = () => {
    const id = pendingSupplierRemoval?._id;
    if (id) setSelectedSupplierIds((prev) => prev.filter((x) => x !== id));
    setPendingSupplierRemoval(null);
  };

  const cancelSupplierRemoval = () => setPendingSupplierRemoval(null);

  const openUserInfoModal = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const openSupplierInfoModal = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierModalOpen(true);
  };

  const renderUser = (user) => {
          const linkedSuppliers = linkedIdsOf(user)
            .map((id) =>
              supplierOptions.find((s) => s._id === id) ||
              (typeof user.supplierId === 'object' && idOf(user.supplierId) === id ? user.supplierId : null)
            )
            .filter(Boolean);

          const isCurrentUser = user._id === currentUserId;

    if (isMobile) {
      const isOpen = expandedCardId === user._id;
      const primary = linkedSuppliers[0];
      const btnBase = 'inline-flex items-center gap-1 px-3 py-1 rounded text-white whitespace-nowrap text-sm';
      return (
        <li key={user._id} className={`min-w-0 border rounded-md bg-white shadow-sm p-3 text-sm ${isCurrentUser ? 'bg-gray-100 text-gray-400' : ''}`}>
          <div className="flex items-start gap-2 min-w-0">
            <div className="min-w-0 flex-1">
              <button type="button" onClick={() => openUserInfoModal(user)} className="block max-w-full truncate text-left font-medium text-blue-600 underline hover:text-blue-800" title={user.username}>{user.username}</button>
              <div className="text-gray-600 truncate" title={user.email || ''}>{user.email || '-'}</div>
            </div>
            <button type="button" onClick={() => setExpandedCardId(isOpen ? null : user._id)} aria-expanded={isOpen} aria-label={t('adminUsers.toggleDetails')} className="shrink-0 p-1 text-blue-600 focus:outline-none">
              <i className={`fas ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden="true" />
            </button>
          </div>
          <div className="mt-2 min-w-0 text-xs text-gray-600">
            <div>{t('adminUsers.role_' + user.role, { defaultValue: user.role })}</div>
            {primary && <div className="truncate" title={primary.displayName || primary.name}>{primary.displayName || primary.name}</div>}
          </div>
          {isOpen && (
            <div className="mt-3 border-t border-gray-200 pt-2 space-y-2 min-w-0">
              <div className="text-xs">
                <div className="font-medium text-gray-700">{t('adminUsers.supplier')}</div>
                {linkedSuppliers.length > 0 ? linkedSuppliers.map((s) => (
                  <button key={s._id} type="button" onClick={() => openSupplierInfoModal(s)} className="block max-w-full truncate text-left text-blue-600 underline hover:text-blue-800" title={s.displayName || s.name}>{s.displayName || s.name}</button>
                )) : <span className="text-gray-400">—</span>}
              </div>
              {user.delegatedGroups?.length > 0 && (
                <button type="button" onClick={() => setDelegationUser(user)} className="px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs whitespace-nowrap hover:underline"
                  title={user.delegatedGroups.map((g) => `${g.name}: ${g.permissions.join(', ')}`).join('\n')}
                >
                  {t('adminUsers.delegated', { count: user.delegatedGroups.length })}
                </button>
              )}
              <div className="flex flex-wrap gap-2">
                <button onClick={() => handleEdit(user)} disabled={isCurrentUser} className={`${btnBase} ${isCurrentUser ? 'bg-gray-400 cursor-not-allowed' : 'bg-yellow-500'}`}>
                  <FaEdit /> {t('adminUsers.edit')}
                </button>
                {isSuperAdmin && (
                  <button onClick={() => handleDeleteClick(user)} disabled={isCurrentUser} className={`${btnBase} ${isCurrentUser ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600'}`}>
                    <FaTrash /> {t('adminUsers.delete')}
                  </button>
                )}
                <button onClick={() => openUserInfoModal(user)} className="inline-flex items-center gap-1 bg-indigo-500 text-white px-3 py-1 rounded whitespace-nowrap text-sm">
                  <FaInfoCircle /> {t('adminUsers.info')}
                </button>
              </div>
            </div>
          )}
        </li>
      );
    }
          return (
            <tr key={user._id} className={`border-t hover:bg-gray-50 align-top ${isCurrentUser ? 'bg-gray-100 text-gray-400' : ''}`}>
              <td className="p-3 text-left">
                <button
                  onClick={() => openUserInfoModal(user)}
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  <TruncatedText text={user.username} maxWidthClass="max-w-[10rem]" />
                </button>
              </td>
              <td className="p-3 text-left"><TruncatedText text={user.email || '-'} maxWidthClass="max-w-[16rem]" /></td>
              <td className="p-3 text-left">
                {t('adminUsers.role_' + user.role, { defaultValue: user.role })}
                {user.delegatedGroups?.length > 0 && (
                  <button type="button" onClick={() => setDelegationUser(user)}
                    className="ml-2 px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs whitespace-nowrap hover:underline"
                    title={user.delegatedGroups.map((g) => `${g.name}: ${g.permissions.join(', ')}`).join('\n')}
                  >
                    {t('adminUsers.delegated', { count: user.delegatedGroups.length })}
                  </button>
                )}
              </td>
              <td className="p-3 text-left">
                {linkedSuppliers.length > 0 ? (
                  linkedSuppliers.map((s) => (
                    <button
                      key={s._id}
                      onClick={() => openSupplierInfoModal(s)}
                      className="block text-blue-600 underline hover:text-blue-800"
                    >
                      <TruncatedText text={s.displayName || s.name} maxWidthClass="max-w-[10rem]" />
                    </button>
                  ))
                ) : (
                  <button disabled className="text-gray-400 cursor-not-allowed">—</button>
                )}
              </td>
              <td className="p-3">
                <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleEdit(user)}
                  disabled={isCurrentUser}
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded text-white whitespace-nowrap ${isCurrentUser ? 'bg-gray-400 cursor-not-allowed' : 'bg-yellow-500'}`}
                >
                  <FaEdit /> {t('adminUsers.edit')}
                </button>
                {isSuperAdmin && (
                  <button
                    onClick={() => handleDeleteClick(user)}
                    disabled={isCurrentUser}
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded text-white whitespace-nowrap ${isCurrentUser ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600'}`}
                  >
                    <FaTrash /> {t('adminUsers.delete')}
                  </button>
                )}
                <button
                  onClick={() => openUserInfoModal(user)}
                  className="inline-flex items-center gap-1 bg-indigo-500 text-white px-3 py-1 rounded whitespace-nowrap"
                >
                  <FaInfoCircle /> {t('adminUsers.info')}
                </button>
                </div>
              </td>
            </tr>
          );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-7xl mx-auto bg-white shadow p-6 rounded-md">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4">{t('adminUsers.title')}</h1>

        {/* Search */}
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative flex-grow">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t('adminUsers.search')}
              className="border p-2 w-full pr-10 rounded"
            />
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">
              <FaSearch />
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        <form onSubmit={handleSubmit} className="space-y-2 mb-6 bg-gray-50 p-4 rounded">
          <h3 className="text-lg font-semibold">
            {editingUser ? t('adminUsers.editUser') : t('adminUsers.addNew')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('adminUsers.username')}</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('adminUsers.username')}
                maxLength={NAME_LIMITS.username.max}
                readOnly={!!editingUser && !isSuperAdmin}
                className={`border p-2 w-full rounded ${editingUser && !isSuperAdmin ? 'bg-gray-100 text-gray-500' : ''}`}
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('adminUsers.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('adminUsers.email')}
                readOnly={!!editingUser && !isSuperAdmin}
                title={editingUser && !isSuperAdmin ? 'Only a Super Admin can change email' : undefined}
                className={`border p-2 w-full rounded ${editingUser && !isSuperAdmin ? 'bg-gray-100 text-gray-500' : ''}`}
              />
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('adminUsers.role')}</label>
              {/* Only a superadmin can change global roles; the backend enforces this too */}
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={!isSuperAdmin}
                title={isSuperAdmin ? undefined : 'Only a Super Admin can change roles'}
                className={`border p-2 w-full rounded ${isSuperAdmin ? '' : 'bg-gray-100 text-gray-500 cursor-not-allowed'}`}
              >
                {isSuperAdmin && <option value="superadmin">{t('adminUsers.superAdmin')}</option>}
                <option value="supplieradmin" disabled={!isSuperAdmin}>{t('adminUsers.supplierAdmin')}</option>
                <option value="member">{t('adminUsers.member')}</option>
              </select>
            </div>

            {/* Supplier */}
          <div>
  <label className="block text-sm font-medium mb-1">{t('adminUsers.supplier')}</label>
  {(
    <div className="border rounded p-2 max-h-40 overflow-y-auto">
      {supplierOptions.length === 0 && <p className="text-sm text-gray-500">{t('adminUsers.noSuppliers')}</p>}
      {supplierOptions.map((s) => (
        <label key={s._id} className="flex items-center gap-2 text-sm py-0.5 min-w-0">
          <input
            type="checkbox"
            checked={selectedSupplierIds.includes(s._id)}
            onChange={(e) => {
              if (!e.target.checked) {
                // A user must keep at least one supplier (links outside a supplier admin's list still count)
                const mySupplierIds = mySuppliers.map((m) => m._id);
                const keptOutside =
                  !isSuperAdmin && editingUser
                    ? linkedIdsOf(editingUser).filter((id) => !mySupplierIds.includes(id))
                    : [];
                const remaining = new Set([...selectedSupplierIds.filter((id) => id !== s._id), ...keptOutside]);
                if (role !== 'superadmin' && remaining.size === 0) {
                  toast.error(t('adminUsers.keepOneSupplier'));
                  return;
                }
                // Unchecking a supplier the user is already linked to must be confirmed first
                if (editingUser && linkedIdsOf(editingUser).includes(s._id)) {
                  setPendingSupplierRemoval(s);
                  return;
                }
              }
              setSelectedSupplierIds((prev) =>
                e.target.checked ? [...prev, s._id] : prev.filter((id) => id !== s._id)
              );
            }}
          />
          <TruncatedText text={s.displayName || s.name} maxWidthClass="max-w-full md:max-w-[14rem]" className="min-w-0" />
        </label>
      ))}
      <p className="text-xs text-gray-500 mt-1">{t('adminUsers.primaryHint')}</p>
    </div>
  )}
</div>

          </div>

          {/* Form Buttons */}
          <div className="flex flex-wrap gap-2 mt-2">
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded"
              disabled={loading || editingUser?._id === currentUserId}
            >
              {editingUser ? t('adminUsers.updateUser') : t('adminUsers.addUser')}
            </button>
            {editingUser && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-500 text-white px-4 py-2 rounded"
              >
                {t('profile.cancel')}
              </button>
            )}
          </div>
        </form>

        {/* Users Table */}
        <ResultCount total={sortedUsers.length} page={currentPage} pageSize={USERS_PER_PAGE} />
        {isMobile ? (
          loading ? <p className="p-4 text-center">{t('adminUsers.loading')}</p>
          : currentUsers.length > 0 ? <ul className="space-y-3">{currentUsers.map(renderUser)}</ul>
          : <p className="p-4 text-center text-gray-600">{users.length === 0 ? t('adminUsers.noUsers') : t('adminUsers.noMatch')}</p>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] bg-white border rounded shadow">
            <thead>
              <tr className="bg-gray-200">
                <th className="p-3 text-left w-40 cursor-pointer" onClick={() => sortByKey('username')}>{t('adminUsers.username')}{sortArrow('username')}</th>
                <th className="p-3 text-left w-56 cursor-pointer" onClick={() => sortByKey('email')}>{t('adminUsers.email')}{sortArrow('email')}</th>
                <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('role')}>{t('adminUsers.role')}{sortArrow('role')}</th>
                <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('supplier')}>{t('adminUsers.supplier')}{sortArrow('supplier')}</th>
                <th className="p-3 text-left">{t('adminUsers.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="border p-4 text-center">{t('adminUsers.loading')}</td>
                </tr>
              ) : currentUsers.length > 0 ? (
                currentUsers.map(renderUser)
              ) : (
                <tr>
                  <td colSpan={5} className="border p-4 text-center text-gray-600">
                    {users.length === 0 ? t('adminUsers.noUsers') : t('adminUsers.noMatch')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        <Pagination page={currentPage} pageCount={totalPages} onChange={setCurrentPage} />

        {/* Modals */}
        <UserInfoModal user={selectedUser} isOpen={modalOpen} onClose={() => setModalOpen(false)} />
        <InfoModal
          isOpen={!!delegationUser}
          onClose={() => setDelegationUser(null)}
          title={t('adminUsers.delegatedTitle', { name: delegationUser?.username || '' })}
        >
          <ul className="list-disc pl-5 space-y-1">
            {(delegationUser?.delegatedGroups || []).map((g) => (
              <li key={g._id || g.name}>
                <span className="font-medium">{g.name}</span>
                {g.permissions?.length > 0 && <span className="text-gray-600"> — {g.permissions.join(', ')}</span>}
              </li>
            ))}
          </ul>
        </InfoModal>
        <SupplierInfoModal trustedContact supplier={selectedSupplier} isOpen={supplierModalOpen} onClose={() => setSupplierModalOpen(false)} />
        <ConfirmationModal
          isOpen={!!pendingSupplierRemoval}
          title={t('adminUsers.removeTitle')}
          message={t('adminUsers.removeMessage', { user: editingUser?.username || '', supplier: pendingSupplierRemoval?.displayName || pendingSupplierRemoval?.name || '' })}
          confirmLabel={t('adminUsers.removeConfirm')}
          cancelLabel={t('profile.cancel')}
          onConfirm={confirmSupplierRemoval}
          onClose={cancelSupplierRemoval}
        />
        <ConfirmationModal
          isOpen={showConfirm}
          title={t('adminUsers.deleteTitle')}
          confirmLabel={t('common.confirm')}
          cancelLabel={t('profile.cancel')}
          message={t('adminUsers.deleteMessage', { user: userToDelete?.username })}
          onConfirm={confirmDelete}
          onClose={cancelDelete}
          username={userToDelete?.username}
        />

      </div>
    </div>
  );
}
